import { Box, BoxProps } from "@chakra-ui/react";
import { ReactPortal } from "components/portal";
import { useRef, useState } from "react";
import { createEventListener, makeEvent } from "utils/events";
import { UseContextMenuOptions } from "./types";

export const makeContextMenu = ({
  content,
  contextMenuStyles,
  id,
  e,
  anchor,
}: UseContextMenuOptions) => {
  makeEvent("createContextMenu", {
    content: content,
    contextMenuStyles: contextMenuStyles,
    id: id,
    e: e,
    anchor: anchor,
  });
};

export function ContextMenuContainer() {
  const [contextMenuElement, setContextMenuElement] =
    useState<JSX.Element | null>(null);
  const menuIdRef = useRef<string | undefined | null>(null);
  const [contextMenuStyles, setContextMenuStyles] = useState<
    BoxProps | undefined
  >(undefined);

  const menuRef = useRef<HTMLDivElement | null>(null);
  // Menu ancorado num elemento: mostra a ponta de balão.
  const [hasArrow, setHasArrow] = useState(false);

  const isOverRef = useRef(false);

  createEventListener("createContextMenu", (options: UseContextMenuOptions) => {
    const isClickEvent = options?.e?.type === "click";
    if (options.id && options.id === menuIdRef.current && !isClickEvent) return;

    setContextMenuElement(options.content);
    setContextMenuStyles(options.contextMenuStyles);
    menuIdRef.current = options.id;

    const e = options.e;
    const menu = document.getElementById("context-menu");
    if (!menu) return;

    menu.style.display = "block";

    const anchor = options.anchor;
    if (!anchor) {
      setHasArrow(false);
      menu.style.visibility = "visible";
      menu.style.top = `${e.clientY}px`;
      menu.style.left = `${e.clientX}px`;
      return;
    }

    // Abaixo do elemento, começando um pouco à esquerda dele, com a ponta do
    // balão no centro do elemento. Se não couber na tela, desliza para a
    // esquerda e a ponta acompanha. Se não couber embaixo e houver mais
    // espaço em cima, abre acima do elemento, com a ponta virada para baixo.
    setHasArrow(true);
    const { rect } = anchor;
    const offsetY = anchor.offsetY ?? 10;
    const offsetX = anchor.offsetX ?? -16;
    const margem = 8;
    menu.style.top = `${rect.bottom + offsetY}px`;
    menu.style.left = `${rect.left + offsetX}px`;
    // Invisível até medir, para não aparecer embaixo e pular para cima.
    menu.style.visibility = "hidden";
    // O tamanho real só existe depois que o React desenha o conteúdo novo;
    // um frame depois, ajusta para caber na tela e posiciona a ponta.
    requestAnimationFrame(() => {
      const largura = menu.offsetWidth;
      const altura = menu.offsetHeight;

      let left = rect.left + offsetX;
      left = Math.min(left, window.innerWidth - margem - largura);
      left = Math.max(left, margem);
      const centroElemento = rect.left + rect.width / 2;
      const arrowLeft = Math.min(
        Math.max(centroElemento - left, 14),
        Math.max(largura - 14, 14),
      );

      const espacoAbaixo = window.innerHeight - margem - (rect.bottom + offsetY);
      const espacoAcima = rect.top - offsetY - margem;
      const abreParaCima = altura > espacoAbaixo && espacoAcima > espacoAbaixo;
      const top = abreParaCima
        ? Math.max(margem, rect.top - offsetY - altura)
        : rect.bottom + offsetY;

      menu.style.left = `${left}px`;
      menu.style.top = `${top}px`;
      menu.style.setProperty("--context-menu-arrow-left", `${arrowLeft}px`);
      // Lado da ponta direto no DOM, no mesmo frame da posição.
      menu.dataset.arrowSide = abreParaCima ? "bottom" : "top";
      menu.style.visibility = "visible";
    });
  });

  createEventListener("closeContextMenu", () => {
    const timeout = setTimeout(() => {
      if (isOverRef.current) return;

      const menu = document.getElementById("context-menu");
      if (menu) menu.style.display = "none";
      menuIdRef.current = null;
    }, 100);
    return () => clearTimeout(timeout);
  });

  return (
    <ReactPortal wrapperId="react-portal-context-menu-container">
      <Box
        id="context-menu"
        position="fixed"
        display="none"
        zIndex={2000}
        bg="white"
        borderRadius="md"
        boxShadow="lg"
        minW="160px"
        // Sem borda por padrão; quem chama pode passar uma em
        // contextMenuStyles (com a cor na própria declaração), e a ponta do
        // balão herda a mesma cor.
        border="0 solid transparent"
        ref={menuRef}
        // Ponta de balão: um quadrado branco girado, meio para fora do topo,
        // na posição horizontal calculada ao abrir. As duas arestas de cima
        // repetem a borda do card, para a ponta parecer parte dele. Quando o
        // menu abre acima do elemento (data-arrow-side="bottom"), a ponta
        // vai para a base e repete as arestas de baixo.
        sx={
          hasArrow
            ? {
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: "-7px",
                  left: "var(--context-menu-arrow-left, 24px)",
                  w: "12px",
                  h: "12px",
                  bg: "white",
                  transform: "translateX(-50%) rotate(45deg)",
                  borderStyle: "solid",
                  borderWidth: "1px 0 0 1px",
                  borderColor: "inherit",
                  borderRadius: "2px 0 0 0",
                  boxShadow: "-2px -2px 4px rgba(0, 0, 0, 0.06)",
                  pointerEvents: "none",
                },
                "&[data-arrow-side='bottom']::before": {
                  top: "auto",
                  bottom: "-7px",
                  borderWidth: "0 1px 1px 0",
                  borderRadius: "0 0 2px 0",
                  boxShadow: "2px 2px 4px rgba(0, 0, 0, 0.06)",
                },
              }
            : undefined
        }
        {...contextMenuStyles}
        onMouseEnter={(e) => {
          isOverRef.current = true;
          contextMenuStyles?.onMouseEnter?.(e);
        }}
        onMouseLeave={(e) => {
          isOverRef.current = false;
          contextMenuStyles?.onMouseLeave?.(e);
        }}
      >
        {contextMenuElement}
      </Box>
    </ReactPortal>
  );
}
